import '../outline-container/outline-container';
import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './outline-content-card.css.lit';
import { OutlineElement } from '../outline-element/outline-element';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';
import { SlotController } from '../../controllers/slot-controller';
import { SlotsController } from '../../controllers/slots-controller';

export type AllowedColors =
  | 'gray'
  | 'light-blue'
  | 'blue'
  | 'green'
  | 'white'
  | 'transparent';
export type ImagePositions = 'top' | 'left' | 'right';
export type ButtonStyles = 'primary' | 'secondary';
export type ContentAlignment = 'left' | 'center' | 'right';
export type ContentMode = 'compact' | 'spacious';
export type TitleMode = 'normal' | 'large';

/**
 * The Outline Content Card component.
 *
 * @element outline-content-card
 * @extends OutlineElement
 * @since 1.0.0
 * @slot image - The image slot.
 * @slot eyebrow - The eyebrow slot.
 * @slot summary - The summary slot.
 * @slot content - The primary content slot.
 * @slot title - The title slot.
 * @slot cta - The CTA slot.
 */
@customElement('outline-content-card')
export class OutlineContentCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(
    this, // This, the host element.
    false // To shift or not to shift LightDom nodes to ShadowDOM.
  );

  slotsFancy = new SlotsController(this);

  /**
   * The color class to apply to the header slot.
   */
  @property({ type: String, attribute: 'card-background' })
  cardBackground: AllowedColors;

  /**
   * The alignment of content item(s).
   * Any additional alignment properties such as ctaAlignment
   * will by default use the inherited contentAlignment
   * if they are not defined.
   */
  @property({ type: String, attribute: 'content-alignment' })
  contentAlignment: ContentAlignment = 'left';

  /**
   * The spacing mode for the element.
   * @
   */
  @property({ type: String, attribute: 'content-mode' })
  contentMode: ContentMode = 'compact';

  /**
   * The size of the title.
   * @todo Figure out implementation method for titleMode.
   * This is tricky since items come initially from the LightDOM.
   */
  @property({ type: String, attribute: 'title-mode' })
  titleMode: TitleMode = 'normal';

  /**
   * The alignment of cta element(s).
   */
  @property({ type: String, attribute: 'cta-alignment' })
  ctaAlignment: ContentAlignment;

  /**
   * The alignment of cta element(s).
   */
  @property({ type: String, attribute: 'title-alignment' })
  titleAlignment: ContentAlignment;

  /**
   * The alignment of cta element(s).
   */
  @property({ type: String, attribute: 'eyebrow-alignment' })
  eyebrowAlignment: ContentAlignment;
  /**
   * The button variant to use from idb-button.
   */
  @property({ type: String, attribute: 'button-variant' })
  buttonVariant: ButtonStyles = 'secondary';

  /**
   * The position of the image on larger screens.
   * The responsive default is always that the image
   * will stack above the content in mobile mode.
   */
  @property({ type: String, attribute: 'image-position' })
  imagePosition: ImagePositions = 'top';

  /**
   * Whether or not this card has rounded corners.
   * @todo Implement isRounded in the Storybook story.
   */
  @property({ type: Boolean, attribute: 'is-rounded' })
  isRounded = false;

  /**
   * Whether or not this card has a shadow.
   * @todo Implement isRounded in the Storybook story.
   */
  @property({ type: Boolean, attribute: 'has-shadow' })
  hasShadow = false;

  @state() hasImageSlot: boolean;
  @state() hasEyebrowSlot: boolean;
  @state() hasTitleSlot: boolean;
  @state() hasContentSlot: boolean;
  @state() hasSummarySlot: boolean;
  @state() hasCtaSlot: boolean;

  connectedCallback() {
    super.connectedCallback();
  }

  firstUpdated(): void {
    this.hasImageSlot = this.slots.test('image');
    this.hasEyebrowSlot = this.slots.test('eyebrow');
    this.hasTitleSlot = this.slots.test('title');
    this.hasSummarySlot = this.slots.test('summary');
    this.hasContentSlot = this.slots.test('content');
    this.hasCtaSlot = this.slots.test('cta');
  }

  render(): TemplateResult {
    const cardClasses = {
      'card': true,
      [`image-position--${this.imagePosition}`]: this.imagePosition,
      'outline-shadow': this.hasShadow,
      'outline-rounded-2xl': this.isRounded,
      [`outline-bg-${this.cardBackground}`]: this.cardBackground !== undefined,
      [`content-mode--${this.contentMode}`]: this.contentMode,
    };
    const imageClasses = {
      'card-image': true,
    };
    const contentWrapperClasses = {
      'card-content-wrapper': true,
      [`content-alignment--${this.contentAlignment}`]: this.contentAlignment,
    };
    const eyebrowClasses = {
      'card-eyebrow': true,
      [`eyebrow-alignment--${this.eyebrowAlignment}`]: this.eyebrowAlignment,
    };
    const titleClasses = {
      'card-title': true,
      [`title-alignment--${this.titleAlignment}`]: this.titleAlignment,
      [`title-mode--${this.titleMode}`]: this.titleMode,
    };
    const summaryClasses = {
      'card-summary': true,
    };
    const contentClasses = {
      'card-content': true,
    };
    const ctaClasses = {
      'card-cta': true,
      [`cta-alignment--${this.ctaAlignment}`]: this.ctaAlignment,
    };

    /**
     * @todo Determine the best course for the background.
     *
     * We should determine if this is required edge to edge,
     * which looks a bit funny, or if the image (and content)
     * should be allowed to extend out to full bleed including
     * the container.
     *
     * Swapping the position of the container/section in this
     * render markup allows the background to go full bleed
     * instead of "page width".
     *
     * Removing the container completely allows it ALL
     * to be full bleed.
     */
    return html`
      <section class="${classMap(cardClasses)}">
        ${this.imageTemplate(imageClasses)}
        <section class="${classMap(contentWrapperClasses)}">
          ${this.eyebrowTemplate(eyebrowClasses)}
          ${this.titleTemplate(titleClasses)}
          ${this.summaryTemplate(summaryClasses)}
          ${this.contentTemplate(contentClasses)}
          ${this.ctaTemplate(ctaClasses)}
        </section>
      </section>
    `;
  }

  /**
   * Template partial for the Image slot.
   * @param classes
   * @returns TemplateResult | null
   */
  imageTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasImageSlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.slotsFancy.renderInShadow('image')}
    </section>`;
  }

  /**
   * Template partial for the Eyebrow slot.
   * @param classes
   * @returns TemplateResult | null
   */
  eyebrowTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasEyebrowSlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.slotsFancy.renderInShadow('eyebrow')}
    </section>`;
  }

  /**
   * Template Partials.
   * @todo Consider a refactor here to the conditionalSlotTemplate.
   */

  /**
   * Template partial for the Title slot.
   * @param classes
   * @returns TemplateResult | null
   */
  titleTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasTitleSlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.slotsFancy.renderInShadow('title')}
    </section>`;
  }

  /**
   * Template partial for the Summary slot.
   * @param classes
   * @returns TemplateResult | null
   */
  summaryTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasSummarySlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.slotsFancy.renderInShadow('summary')}
    </section>`;
  }

  /**
   * Template partial for the Content slot.
   * @param classes
   * @returns TemplateResult | null
   */
  contentTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasContentSlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.slotsFancy.renderInShadow('content')}
    </section>`;
  }

  /**
   * Template partial for the CTA slot.
   * @param classes
   * @returns TemplateResult | null
   */
  ctaTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasCtaSlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.slotsFancy.renderInShadow('cta')} 
    </section>`;
  }
}
